import {
	BearerImageError,
	createLatestOnlyRunner,
	fetchBearerImage,
	getNativeToken,
	type LatestOnlyRunner,
	type ProxyFetchOutcome,
	withoutCacheBuster,
} from '@polygraf/shared'
import { useEffect, useRef, useState } from 'react'

/**
 * Longer than the server's own frame timeout (8 s), so a slow camera answers with
 * its real error. Only a request that never settles is ended by this.
 */
const FRAME_FETCH_TIMEOUT_MS = 15_000

/**
 * The `<img src>` for a polled JPG frame. In the app WebView (a native token is
 * injected) a frame on our API is fetched with the bearer and shown as a blob
 * URL: the WebView has no cookie and an `<img>` cannot send a header, so an
 * IP-restricted space refused its own signed-in owner every frame (MU 7. 10.
 * 2026). Elsewhere, or when that fetch fails, it is the plain URL, so a refusal
 * still reaches the `<img>` and its existing error handling.
 *
 * One fetch runs at a time, and only the newest tick waits behind it: a frame
 * slower than the poll interval must still finish, or no picture ever appears.
 * `baseUrl` is `url` without its per-tick cache-buster: when it changes the
 * source changed, so the fetch for the old one is abandoned.
 *
 * With `onFrameFailure` (frame polling of an MJPG service) a failed fetch keeps the
 * last frame and reports what happened. The plain URL is no fallback there: in the
 * app it can only answer 401, which costs a request, logs a false denial and blanks
 * the picture on every failed tick. The fetch also drops the `?t=`, so a CORS
 * preflight can be reused between frames.
 */
export function useBearerImageSrc(
	url: string | null,
	baseUrl: string | null,
	apiUrl: string,
	enabled: boolean,
	onFrameFailure?: (outcome: ProxyFetchOutcome) => void,
) {
	const token = enabled && url ? getNativeToken() : null
	const [src, setSrc] = useState<string | null>(token ? null : url)
	const objectUrlRef = useRef<string | null>(null)

	const runnerRef = useRef<LatestOnlyRunner<string> | null>(null)
	const onFrameFailureRef = useRef(onFrameFailure)
	onFrameFailureRef.current = onFrameFailure

	// biome-ignore lint/correctness/useExhaustiveDependencies: `baseUrl` is the trigger, a new source drops the frame in flight for the old one.
	useEffect(() => {
		if (!token) return
		let disposed = false
		const runner = createLatestOnlyRunner<string>(
			async (frameUrl, signal) => {
				try {
					const requestUrl = onFrameFailureRef.current ? withoutCacheBuster(frameUrl) : frameUrl
					const blob = await fetchBearerImage(requestUrl, apiUrl, token, { signal })
					if (disposed) return
					if (!blob) {
						setSrc(frameUrl)
						return
					}
					// Keep the previous frame's URL alive until the new one replaces it,
					// so the picture never blanks between polls.
					const previous = objectUrlRef.current
					objectUrlRef.current = URL.createObjectURL(blob)
					setSrc(objectUrlRef.current)
					if (previous) URL.revokeObjectURL(previous)
				} catch (error) {
					if (disposed) return
					const report = onFrameFailureRef.current
					if (!report) {
						setSrc(frameUrl)
						return
					}
					report(error instanceof BearerImageError ? error.outcome : { kind: 'unknown' })
				}
			},
			{ timeoutMs: FRAME_FETCH_TIMEOUT_MS },
		)
		runnerRef.current = runner
		return () => {
			disposed = true
			runner.dispose()
			runnerRef.current = null
		}
	}, [baseUrl, apiUrl, token])

	useEffect(() => {
		if (!url || !token) {
			setSrc(url)
			return
		}
		runnerRef.current?.push(url)
	}, [url, token])

	useEffect(
		() => () => {
			if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
		},
		[],
	)

	return src
}
